import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowUpRight, Film, ShieldCheck, Download, BadgeCheck, Package, MessageCircle,
  Bell, Check, Loader2, X, ChevronLeft, ChevronRight,
} from "lucide-react";
import { AeroNav } from "@/components/aero/AeroNav";
import { AeroFooter } from "@/components/aero/AeroFooter";
import { WhatsAppFloat } from "@/components/cine/WhatsAppFloat";
import { STOCK_CITIES } from "@/constants/products";
import { SOCIALS } from "@/constants/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.16, 1, 0.3, 1];


const SectionHead = ({ eyebrow, title, sub, testid, dark = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.8, ease }}
    className="max-w-2xl"
    data-testid={testid}
  >
    <p className={`font-mono text-[12px] tracking-[0.35em] uppercase ${dark ? "text-[#C9A227]" : "text-[#4F6D8A]"}`}>{eyebrow}</p>
    <h2 className={`mt-4 font-serif text-[32px] md:text-[44px] leading-[1.05] ${dark ? "text-white" : "text-[#111827]"}`}>{title}</h2>
    <span className={`mt-5 block w-14 h-[2px] ${dark ? "bg-gradient-to-r from-[#C9A227] to-[#FF7A1A]" : "bg-[#4F6D8A]"}`} />
    {sub && <p className={`mt-5 leading-relaxed ${dark ? "text-white/55" : "text-[#6B7280]"}`}>{sub}</p>}
  </motion.div>
);

export default function ProductsPage() {
  const navigate = useNavigate();
  const [allProducts, setAllProducts] = useState([]);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [waBase, setWaBase] = useState(null);
  // hero carousel
  const [slide, setSlide] = useState(0);
  const [slidePaused, setSlidePaused] = useState(false);
  // launch notify (coming soon products)
  const [notifyFor, setNotifyFor] = useState(null);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifySending, setNotifySending] = useState(false);
  const [notifyDone, setNotifyDone] = useState(false);

  useEffect(() => {
    if (slidePaused || allProducts.length < 2) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % allProducts.length), 5000);
    return () => clearInterval(t);
  }, [slidePaused, allProducts.length]);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${API}/products/catalog`)
      .then((r) => {
        setAllProducts(r.data);
      })
      .catch(() => {})
      .finally(() => setCatalogLoaded(true));
    axios
      .get(`${API}/config`)
      .then((r) => r.data.whatsapp_number && setWaBase(r.data.whatsapp_number))
      .catch(() => {});
  }, []);

  const contactHref = (item) =>
    waBase
      ? `https://wa.me/${waBase}?text=${encodeURIComponent(`Hi Akhilesh, mujhe "${item}" ke baare mein baat karni hai. Details share karo.`)}`
      : SOCIALS.instagram;

  // hero carousel slides — live (purchasable) products pehle
  const slides = [...allProducts].sort((a, b) => (b.status === "live") - (a.status === "live"));

  const submitNotify = async () => {
    if (!/^\S+@\S+\.\S+$/.test(notifyEmail)) {
      toast.error("Valid email daalo");
      return;
    }
    setNotifySending(true);
    try {
      await axios.post(`${API}/leads`, {
        name: `${notifyFor?.name || "Product"} Notify`,
        email: notifyEmail.trim(),
        resource: notifyFor?.id === "dji-field-guide" ? "ebook-field-guide-2026" : `notify-${notifyFor?.id || "product"}`,
      });
      setNotifyDone(true);
      toast.success("Done! Launch par sabse pehle email milega.");
    } catch {
      toast.error("Kuch gadbad ho gayi — dobara try karo.");
    } finally {
      setNotifySending(false);
    }
  };

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="bg-[#F8F6F2] text-[#111827] overflow-x-clip"
      data-testid="products-page"
    >
      <AeroNav />

      {/* Hero — featured products carousel (all active products) */}
      <section className="relative bg-[#0A0908] pt-28 md:pt-36 pb-14 md:pb-16 overflow-hidden" data-testid="products-hero">
        <div aria-hidden className="pointer-events-none absolute -top-32 left-1/4 w-[500px] h-[500px] rounded-full bg-[#C9A227]/[0.09] blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute bottom-0 right-0 w-[380px] h-[380px] rounded-full bg-[#FF7A1A]/[0.07] blur-3xl" />
        <div className="relative z-10 max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="flex items-center justify-between gap-6 flex-wrap">
            <p className="font-mono text-[12px] tracking-[0.35em] uppercase text-[#C9A227] flex items-center gap-4">
              <span className="w-10 h-px bg-[#C9A227]/60 inline-block" /> Creator Store
            </p>
            <Link
              to="/my-orders"
              data-testid="products-my-orders-link"
              className="font-mono text-[11px] font-bold tracking-[0.2em] uppercase text-white/60 hover:text-[#F5C04E] underline underline-offset-4 decoration-white/20 hover:decoration-[#C9A227] transition-colors flex items-center gap-2"
            >
              My Orders <ArrowUpRight size={13} />
            </Link>
          </div>

          {!catalogLoaded ? (
            <div className="relative mt-8" data-testid="carousel-skeleton">
              <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center animate-pulse">
                <div>
                  <div className="h-8 w-32 rounded-full bg-white/10" />
                  <div className="mt-6 h-12 w-4/5 rounded-2xl bg-white/10" />
                  <div className="mt-3 h-12 w-3/5 rounded-2xl bg-white/10" />
                  <div className="mt-5 h-4 w-full max-w-lg rounded-lg bg-white/[0.07]" />
                  <div className="mt-2 h-4 w-2/3 max-w-lg rounded-lg bg-white/[0.07]" />
                  <div className="mt-7 h-12 w-44 rounded-full bg-[#C9A227]/20" />
                </div>
                <div className="mx-auto w-full max-w-[420px] aspect-[4/5] rounded-[18px] bg-white/[0.06] border border-white/10" />
              </div>
            </div>
          ) : allProducts.length === 0 ? (
            <h1 className="mt-8 font-serif text-[44px] md:text-[68px] leading-[1.02] text-white">Products</h1>
          ) : (
            <div
              className="relative mt-8"
              onMouseEnter={() => setSlidePaused(true)}
              onMouseLeave={() => setSlidePaused(false)}
              data-testid="products-carousel"
            >
              <AnimatePresence mode="wait">
                {(() => {
                  const p = slides[slide % slides.length];
                  const live = p.status === "live";
                  const eb = p.early_bird;
                  const ebActive = eb && eb.remaining > 0;
                  const price = ebActive ? eb.early_price : p.price;
                  return (
                    <motion.div
                      key={p.id}
                      initial={{ opacity: 0, x: 60 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -60 }}
                      transition={{ duration: 0.55, ease }}
                      className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center"
                      data-testid={`product-slide-${p.id}`}
                    >
                      <div>
                        <span className={`inline-flex items-center gap-2 font-mono text-[9px] font-bold tracking-[0.25em] uppercase px-4 py-2 rounded-full ${live ? "bg-gradient-to-r from-[#C9A227] to-[#FF7A1A] text-[#0A0908]" : "border border-[#C9A227]/50 bg-[#C9A227]/10 text-[#F5C04E]"}`}>
                          {live ? <><BadgeCheck size={11} /> Live Now</> : <><Bell size={11} /> Coming Soon</>}
                        </span>
                        <h1 className="mt-6 font-display font-black text-4xl md:text-6xl leading-[1.02] text-white tracking-tight" data-testid={`slide-name-${p.id}`}>
                          {p.name}
                        </h1>
                        <p className="mt-4 text-white/55 leading-relaxed max-w-lg line-clamp-3">{p.description}</p>
                        <div className="mt-6 flex items-end gap-4">
                          <p className="font-display font-black text-4xl md:text-5xl bg-gradient-to-r from-[#C9A227] via-[#F5C04E] to-[#FF7A1A] bg-clip-text text-transparent" data-testid={`slide-price-${p.id}`}>
                            ₹{Number(price).toLocaleString("en-IN")}
                          </p>
                          {ebActive && <p className="font-display font-bold text-xl text-white/30 line-through mb-1">₹{Number(p.price).toLocaleString("en-IN")}</p>}
                        </div>
                        {ebActive && (
                          <p className="mt-2 font-mono text-[10px] tracking-[0.18em] uppercase text-[#C9A227]/90" data-testid={`slide-counter-${p.id}`}>
                            {eb.remaining} of {eb.limit} early bird spots remaining
                          </p>
                        )}
                        <div className="mt-7 flex flex-wrap gap-4">
                          {live ? (
                            <button
                              data-testid={`slide-buy-${p.id}`}
                              onClick={() => navigate(`/products/${p.id}`)}
                              className="inline-flex items-center gap-2.5 bg-gradient-to-r from-[#C9A227] to-[#FF7A1A] text-[#0A0908] font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-8 py-4 rounded-full hover:shadow-[0_15px_40px_-10px_rgba(201,162,39,0.6)] hover:-translate-y-0.5 transition-all duration-300"
                            >
                              Buy Now <ArrowUpRight size={13} />
                            </button>
                          ) : (
                            <button
                              data-testid={`slide-notify-${p.id}`}
                              onClick={() => { setNotifyFor(p); setNotifyDone(false); setNotifyEmail(""); setNotifyOpen(true); }}
                              className="inline-flex items-center gap-2 border border-[#C9A227]/40 text-[#F5C04E] font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-8 py-4 rounded-full hover:bg-[#C9A227] hover:text-[#0A0908] hover:border-[#C9A227] transition-all duration-300"
                            >
                              <Bell size={12} /> Notify on Launch
                            </button>
                          )}
                          <button
                            data-testid={`slide-details-${p.id}`}
                            onClick={() => navigate(`/products/${p.id}`)}
                            className="inline-flex items-center gap-2 border border-white/20 text-white/70 font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-8 py-4 rounded-full hover:border-[#C9A227]/60 hover:text-[#F5C04E] transition-all duration-300"
                          >
                            View Details
                          </button>
                        </div>
                      </div>
                      <div className="relative mx-auto w-full max-w-[420px]">
                        <div aria-hidden className="absolute -inset-4 rounded-[28px] bg-gradient-to-br from-[#C9A227]/25 to-[#FF7A1A]/12 blur-2xl" />
                        {p.thumbnail_url ? (
                          <img src={p.thumbnail_url} alt={`${p.name} cover`} data-testid={`slide-cover-${p.id}`} className="relative w-full aspect-[4/5] object-cover rounded-[18px] border border-[#C9A227]/25 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.8)]" />
                        ) : (
                          <div data-testid={`slide-cover-${p.id}`} className="relative w-full aspect-[4/5] rounded-[18px] border border-white/10 bg-gradient-to-br from-[#171410] to-[#0A0908] flex items-center justify-center">
                            <Package size={64} strokeWidth={0.8} className="text-[#C9A227]/50" />
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })()}
              </AnimatePresence>

              {/* controls */}
              {slides.length > 1 && (
                <div className="mt-9 flex items-center gap-5">
                  <button
                    data-testid="carousel-prev"
                    aria-label="Previous product"
                    onClick={() => setSlide((s) => (s - 1 + slides.length) % slides.length)}
                    className="w-10 h-10 rounded-full border border-white/20 text-white/60 hover:border-[#C9A227] hover:text-[#F5C04E] flex items-center justify-center transition-colors"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    data-testid="carousel-next"
                    aria-label="Next product"
                    onClick={() => setSlide((s) => (s + 1) % slides.length)}
                    className="w-10 h-10 rounded-full border border-white/20 text-white/60 hover:border-[#C9A227] hover:text-[#F5C04E] flex items-center justify-center transition-colors"
                  >
                    <ChevronRight size={16} />
                  </button>
                  <div className="flex items-center gap-2.5 ml-2">
                    {slides.map((p, i) => (
                      <button
                        key={p.id}
                        data-testid={`carousel-dot-${i}`}
                        aria-label={`Go to ${p.name}`}
                        onClick={() => setSlide(i)}
                        className={`h-1.5 rounded-full transition-all duration-300 ${i === slide % slides.length ? "w-8 bg-gradient-to-r from-[#C9A227] to-[#FF7A1A]" : "w-2.5 bg-white/20 hover:bg-white/40"}`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="mt-10 pt-7 border-t border-white/10 flex flex-wrap gap-x-8 gap-y-3 items-center">
            {[
              { icon: Download, t: "Instant Delivery" },
              { icon: BadgeCheck, t: "Made From Real Shoots" },
              { icon: ShieldCheck, t: "Secure Razorpay Checkout" },
            ].map((x) => {
              const Icon = x.icon;
              return (
                <p key={x.t} className="font-mono text-[11px] tracking-[0.2em] uppercase text-white/40 flex items-center gap-2.5">
                  <Icon size={13} className="text-[#C9A227]" /> {x.t}
                </p>
              );
            })}
          </div>
        </div>
      </section>

      {/* All Products grid — dark cinematic, DB-driven */}
      <section className="relative bg-[#0A0908] pb-[90px] md:pb-[120px] pt-4 md:pt-8 overflow-hidden" data-testid="products-grid-section">
        <div aria-hidden className="pointer-events-none absolute top-20 right-0 w-[400px] h-[400px] rounded-full bg-[#C9A227]/[0.06] blur-3xl" />
        <div className="relative z-10 max-w-[1320px] mx-auto px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease }}
            className="max-w-2xl"
          >
            <p className="font-mono text-[12px] tracking-[0.35em] uppercase text-[#C9A227]">All Products</p>
            <h2 className="mt-4 font-serif text-[32px] md:text-[44px] leading-[1.05] text-white">The Complete Toolkit</h2>
            <span className="mt-5 block w-14 h-[2px] bg-gradient-to-r from-[#C9A227] to-[#FF7A1A]" />
            <p className="mt-5 leading-relaxed text-white/55">Saare digital tools ek jagah — real productions se bane, instant delivery ke saath.</p>
          </motion.div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7" data-testid="products-grid">
            {!catalogLoaded &&
              [0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="rounded-[22px] overflow-hidden border border-white/10 bg-[#111009] animate-pulse" data-testid={`grid-skeleton-${i}`}>
                  <div className="aspect-[4/3] bg-white/[0.06]" />
                  <div className="p-6 space-y-3">
                    <div className="h-5 w-3/4 rounded-lg bg-white/10" />
                    <div className="h-3.5 w-full rounded-lg bg-white/[0.06]" />
                    <div className="pt-5 mt-2 border-t border-white/10 flex items-center justify-between">
                      <div className="h-7 w-16 rounded-lg bg-[#C9A227]/15" />
                      <div className="h-10 w-28 rounded-full bg-white/10" />
                    </div>
                  </div>
                </div>
              ))}
            {allProducts.map((p, i) => {
              const live = p.status === "live";
              const eb = p.early_bird;
              const ebActive = eb && eb.remaining > 0;
              const price = ebActive ? eb.early_price : p.price;
              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.55, delay: (i % 3) * 0.08, ease }}
                  data-testid={`grid-card-${p.id}`}
                  className="group relative rounded-[22px] overflow-hidden border border-white/10 bg-[#111009] hover:border-[#C9A227]/40 hover:-translate-y-1.5 hover:shadow-[0_35px_70px_-20px_rgba(201,162,39,0.25)] transition-all duration-300 flex flex-col"
                >
                  {/* cover */}
                  <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-[#171410] to-[#0A0908]">
                    {p.thumbnail_url ? (
                      <img src={p.thumbnail_url} alt={`${p.name} cover`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <span className="absolute inset-0 flex items-center justify-center">
                        <Package size={52} strokeWidth={0.9} className="text-[#C9A227]/40 group-hover:scale-110 transition-transform duration-300" />
                      </span>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#111009] via-transparent to-transparent" />
                    <span className={`absolute top-4 left-4 font-mono text-[8px] font-bold tracking-[0.25em] uppercase px-3.5 py-1.5 rounded-full ${live ? "bg-gradient-to-r from-[#C9A227] to-[#FF7A1A] text-[#0A0908]" : "bg-[#0A0908]/75 backdrop-blur text-[#F5C04E] border border-[#C9A227]/30"}`}>
                      {live ? "Live Now" : "Coming Soon"}
                    </span>
                    {ebActive && (
                      <span className="absolute top-4 right-4 font-mono text-[8px] font-bold tracking-[0.2em] uppercase bg-[#0A0908]/75 backdrop-blur text-[#F5C04E] border border-[#C9A227]/30 px-3 py-1.5 rounded-full">
                        {eb.remaining}/{eb.limit} early bird left
                      </span>
                    )}
                  </div>
                  {/* content */}
                  <div className="p-6 flex flex-col flex-1">
                    <p className="font-display font-bold text-lg text-white leading-snug">{p.name}</p>
                    <p className="mt-2 text-sm text-white/50 leading-relaxed line-clamp-2">{p.description}</p>
                    <div className="flex-1 min-h-[18px]" />
                    <div className="pt-5 border-t border-white/10 flex items-center justify-between gap-4">
                      <div>
                        <p className="font-display font-black text-2xl bg-gradient-to-r from-[#C9A227] via-[#F5C04E] to-[#FF7A1A] bg-clip-text text-transparent" data-testid={`grid-price-${p.id}`}>
                          ₹{Number(price).toLocaleString("en-IN")}
                        </p>
                        {ebActive && <p className="font-display font-bold text-sm text-white/30 line-through">₹{Number(p.price).toLocaleString("en-IN")}</p>}
                      </div>
                      {live ? (
                        <button
                          data-testid={`grid-buy-${p.id}`}
                          onClick={() => navigate(`/products/${p.id}`)}
                          className="inline-flex items-center gap-2 bg-gradient-to-r from-[#C9A227] to-[#FF7A1A] text-[#0A0908] font-mono text-[9px] font-bold tracking-[0.2em] uppercase px-6 py-3.5 rounded-full hover:shadow-[0_12px_35px_-10px_rgba(201,162,39,0.6)] hover:-translate-y-0.5 transition-all duration-300"
                        >
                          Buy Now <ArrowUpRight size={12} />
                        </button>
                      ) : (
                        <button
                          data-testid={`grid-notify-${p.id}`}
                          onClick={() => { setNotifyFor(p); setNotifyDone(false); setNotifyEmail(""); setNotifyOpen(true); }}
                          className="inline-flex items-center gap-2 border border-[#C9A227]/45 text-[#F5C04E] font-mono text-[9px] font-bold tracking-[0.2em] uppercase px-6 py-3.5 rounded-full hover:bg-[#C9A227] hover:text-[#0A0908] hover:border-[#C9A227] transition-all duration-300"
                        >
                          <Bell size={11} /> Notify Me
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stock Shots — dark cinematic city grid */}
      <section className="bg-[#0A0908] py-[90px] md:py-[120px] relative overflow-hidden" data-testid="products-stock">
        <div aria-hidden className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full bg-[#C9A227]/[0.07] blur-3xl" />
        <div className="max-w-[1320px] mx-auto px-6 md:px-10 relative z-10">
          <SectionHead
            dark
            eyebrow="Stock Shots"
            title="Cinematic Aerial Footage — City Wise"
            sub="4K drone footage India ke iconic cities se — commercials, documentaries aur films ke liye. Har city ka pack jald launch hoga."
            testid="products-stock-head"
          />
          <div className="mt-14 grid sm:grid-cols-2 xl:grid-cols-3 gap-7">
            {STOCK_CITIES.map((c, i) => (
              <motion.div
                key={c.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.09, ease }}
                data-testid={`stock-city-${c.name.toLowerCase()}`}
                className="group relative rounded-[22px] overflow-hidden border border-white/10 hover:border-[#C9A227]/40 transition-all duration-500 hover:-translate-y-1.5 shadow-[0_25px_60px_-20px_rgba(0,0,0,0.7)]"
              >
                {/* image */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={c.img}
                    alt={`${c.name} — ${c.landmark} aerial view`}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1200ms] ease-out"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0908] via-[#0A0908]/35 to-transparent" />
                  <span className="absolute top-4 left-4 font-mono text-[8px] font-bold tracking-[0.25em] uppercase bg-[#0A0908]/70 backdrop-blur text-[#F5C04E] border border-[#C9A227]/30 px-3.5 py-1.5 rounded-full flex items-center gap-1.5">
                    <Film size={10} /> {c.landmark}
                  </span>
                </div>
                {/* content */}
                <div className="relative bg-[#0A0908] px-6 pb-6 -mt-10">
                  <h3 className="font-display font-black text-2xl text-white tracking-tight">{c.name}</h3>
                  <p className="mt-1.5 font-mono text-[9px] tracking-[0.3em] uppercase text-[#C9A227]">Cinematic Aerial Footage</p>
                  <a
                    data-testid={`stock-city-btn-${c.name.toLowerCase()}`}
                    href={contactHref(c.name)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-5 inline-flex items-center gap-2 border border-[#C9A227]/45 text-[#F5C04E] font-mono text-[9px] font-bold tracking-[0.22em] uppercase px-6 py-3 rounded-full hover:bg-[#C9A227] hover:text-[#0A0908] hover:border-[#C9A227] transition-all duration-300"
                  >
                    <MessageCircle size={11} /> Contact Us
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* eBook notify modal */}
      <AnimatePresence>
        {notifyOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] bg-[#0A0908]/80 backdrop-blur-sm flex items-center justify-center px-5"
            onClick={() => setNotifyOpen(false)}
            data-testid="ebook-notify-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.97 }}
              transition={{ duration: 0.35, ease }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md bg-[#0A0908] border border-[#C9A227]/25 rounded-[26px] p-8 md:p-10 shadow-[0_50px_100px_-20px_rgba(0,0,0,0.9)]"
            >
              <div aria-hidden className="absolute -top-20 -right-16 w-56 h-56 rounded-full bg-[#C9A227]/15 blur-3xl" />
              <button
                onClick={() => setNotifyOpen(false)}
                aria-label="Close"
                data-testid="ebook-notify-close"
                className="absolute top-5 right-5 w-9 h-9 rounded-full border border-white/15 text-white/60 hover:text-white hover:border-white/40 flex items-center justify-center transition-colors"
              >
                <X size={15} />
              </button>
              <div className="relative z-10">
                <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-[#C9A227]">{notifyFor?.name || "Product"}</p>
                <h3 className="mt-3 font-display font-black text-2xl md:text-3xl text-white leading-tight">
                  {notifyDone ? "You're on the list." : "Get Notified When It's Live"}
                </h3>
                {notifyDone ? (
                  <p data-testid="ebook-notify-success" className="mt-5 flex items-center gap-3 bg-[#C9A227]/10 border border-[#C9A227]/30 text-[#F5C04E] font-mono text-[10px] font-bold tracking-[0.18em] uppercase px-5 py-4 rounded-2xl">
                    <Check size={15} /> Launch par sabse pehle email aayega
                  </p>
                ) : (
                  <>
                    <p className="mt-3 text-white/55 text-sm leading-relaxed">
                      Email chhodo — launch hote hi sabse pehle tumhe pata chalega.
                    </p>
                    <input
                      type="email"
                      value={notifyEmail}
                      onChange={(e) => setNotifyEmail(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && submitNotify()}
                      placeholder="your@email.com"
                      data-testid="ebook-notify-email"
                      className="mt-6 w-full bg-white/[0.06] border border-white/15 focus:border-[#C9A227] outline-none rounded-2xl px-5 py-4 text-sm text-white placeholder:text-white/35 transition-colors"
                    />
                    <button
                      onClick={submitNotify}
                      disabled={notifySending}
                      data-testid="ebook-notify-submit"
                      className="mt-4 w-full inline-flex items-center justify-center gap-2.5 bg-gradient-to-r from-[#C9A227] to-[#FF7A1A] text-[#0A0908] font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-8 py-4 rounded-full hover:shadow-[0_15px_40px_-10px_rgba(201,162,39,0.6)] transition-all duration-300 disabled:opacity-60"
                    >
                      {notifySending ? <Loader2 size={14} className="animate-spin" /> : <Bell size={13} />} Notify Me
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AeroFooter />
      <WhatsAppFloat />
    </motion.main>
  );
}
